/*
PROV: COHINT001.BF.JIATG79MJ3
REQ: SYS-ARCH-15, SYS-ARCH-18
WHY: Shared package module. Updated to inject Firebase ID token as Bearer
     header on all authenticated requests (Phase 1A Firebase Auth migration).
*/

export type ApiEnvelope<T> = {
  ok: boolean;
  data: T | null;
  error: { code: string; message: string; details?: any } | null;
  meta: { request_id: string; correlation_id?: string; trace_id?: string };
};

const CANONICAL_SEED_GROUP_ID = "d742e00b-c9bf-46fb-a11f-fec6463a6c1a";
const CANONICAL_SEED_GROUP_DISPLAY_NAME = "test_seed_group";

export function humanizeKnownIdsInText(text: string): string {
  const raw = String(text || "");
  if (!raw) return raw;

  // Only rewrite in contexts that look user-facing and refer to groups.
  const lower = raw.toLowerCase();
  const looksGroupRelated = lower.includes("group") || lower.includes("group_id") || lower.includes("group-id");
  if (!looksGroupRelated) return raw;
  if (!raw.includes(CANONICAL_SEED_GROUP_ID)) return raw;
  return raw.split(CANONICAL_SEED_GROUP_ID).join(CANONICAL_SEED_GROUP_DISPLAY_NAME);
}

export type ApiError = Error & {
  code?: string;
  requestId?: string;
  correlationId?: string;
  traceId?: string;
  status?: number;
  retryAfter?: number;
};

type LastApiContext = {
  requestId?: string;
  correlationId?: string;
  traceId?: string;
};

let lastApiContext: LastApiContext = {};

// ---------------------------------------------------------------------------
// Bearer token auth (Firebase custom/ID tokens)
// ---------------------------------------------------------------------------
let _authToken: string | null = null;

const AUTH_TOKEN_KEY = "cohorta_auth_token";

export function setAuthToken(token: string | null): void {
  _authToken = token;
  if (typeof window !== "undefined") {
    try {
      if (token) {
        window.sessionStorage.setItem(AUTH_TOKEN_KEY, token);
      } else {
        window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
      }
    } catch {
      // sessionStorage not available (SSR, private browsing)
    }
  }
}

export function getAuthToken(): string | null {
  if (_authToken) return _authToken;
  if (typeof window !== "undefined") {
    try {
      return window.sessionStorage.getItem(AUTH_TOKEN_KEY);
    } catch {
      return null;
    }
  }
  return null;
}

export function clearAuthToken(): void {
  setAuthToken(null);
}

function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  if (!token) return {};
  return { authorization: `Bearer ${token}` };
}

export function getLastApiContext(): LastApiContext {
  return { ...lastApiContext };
}

export type GetJsonOptions = {
  cacheTtlMs?: number;
  cacheKey?: string;
  timeoutMs?: number;
  retries?: number;
  retryBaseDelayMs?: number;
};

type CacheEntry = {
  expiresAt: number;
  value: unknown;
};

const inflightGets = new Map<string, Promise<unknown>>();
const responseCache = new Map<string, CacheEntry>();

function appSurface(): string {
  if (typeof window === "undefined") return "unknown";
  const port = window.location.port;
  if (port === "3001") return "admin";
  if (port === "3002") return "support";
  return "web";
}

function reportClientError(payload: Record<string, any>): void {
  try {
    if (typeof window === "undefined") return;
    const endpoint = "/api/v1/telemetry/client-error";
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }));
      return;
    }
    fetch(endpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

let lastNotFoundReportAt = 0;
const recentNotFoundKeys = new Set<string>();

function randomId(): string {
  try {
    const uuid = (globalThis as any)?.crypto?.randomUUID?.();
    if (uuid) return String(uuid);
  } catch {
    // fall through
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function isAdminOrSupportApp(): boolean {
  if (typeof window === "undefined") return false;
  
  // Check if we're in an admin or support app based on the port or path
  const port = window.location.port;
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // Admin app runs on port 3001, Support app on port 3002
  if (port === "3001" || port === "3002") return true;
  
  // Check for admin/support paths in production
  if (pathname.startsWith("/admin") || pathname.startsWith("/support")) return true;
  
  // Check for admin/support subdomains in production
  if (hostname.startsWith("admin.") || hostname.startsWith("support.")) return true;
  
  return false;
}

function getSuperuserHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  
  // Only use superuser tokens in Admin or Support apps, never in the PWA
  if (!isAdminOrSupportApp()) return {};
  
  try {
    // Prefer session-scoped storage. Avoid persistent storage in production-like environments.
    const token = window.sessionStorage.getItem("cohorta_superuser_token")
      || ((window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
        ? window.localStorage.getItem("cohorta_superuser_token")
        : null);
    if (!token) return {};
    return { "x-superuser-token": token };
  } catch {
    return {};
  }
}

function headersToRecord(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  try {
    if (headers instanceof Headers) {
      return Object.fromEntries(Array.from(headers.entries()));
    }
    if (Array.isArray(headers)) {
      return Object.fromEntries(headers);
    }
  } catch {
    // ignore
  }
  return headers as Record<string, string>;
}

async function requestJson<T>(path: string, options: RequestInit): Promise<T> {
  const mergedHeaders: Record<string, string> = {
    "content-type": "application/json",
    ...getAuthHeaders(),
    ...getSuperuserHeaders(),
    ...headersToRecord(options.headers),
  };

  // Phase 1A: Inject Firebase ID token as Bearer header for authenticated
  // requests. Only applies to the member-facing web app (not admin/support,
  // which continue using privileged session cookies).
  if (!isAdminOrSupportApp() && !mergedHeaders["Authorization"] && !mergedHeaders["authorization"]) {
    try {
      const { getIdToken } = await import("../auth/firebase");
      const idToken = await getIdToken();
      if (idToken) {
        mergedHeaders["Authorization"] = `Bearer ${idToken}`;
      }
    } catch {
      // Firebase module not initialised or not available — continue
      // without Bearer token (falls back to legacy cookie during transition).
    }
  }

  const existingCorrelationKey = Object.keys(mergedHeaders).find((k) => k.toLowerCase() === "x-correlation-id");
  const existingCorrelationValue = existingCorrelationKey ? String(mergedHeaders[existingCorrelationKey] || "") : "";
  if (!existingCorrelationKey || !existingCorrelationValue.trim()) {
    mergedHeaders["x-correlation-id"] = randomId();
  }

  let resp: Response;
  try {
    const timeoutMsRaw = (options as any)?.timeoutMs;
    const timeoutMs = Number.isFinite(Number(timeoutMsRaw)) && Number(timeoutMsRaw) > 0 ? Number(timeoutMsRaw) : 0;

    const { timeoutMs: _ignoredTimeoutMs, ...fetchOptions } = (options as any) || {};

    const controller =
      timeoutMs > 0 && typeof AbortController !== "undefined" ? new AbortController() : undefined;
    const fetchPromise = fetch(path, {
      ...fetchOptions,
      headers: mergedHeaders,
      credentials: "include",
      signal: controller?.signal,
    });

    if (controller && timeoutMs > 0) {
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        resp = await fetchPromise;
      } finally {
        clearTimeout(timeoutId);
      }
    } else if (timeoutMs > 0) {
      // Fallback for environments without AbortController (older webviews / browsers).
      // We can't cancel the underlying request, but we can stop awaiting it and
      // surface a timeout to the caller so UI doesn't spin forever.
      let didTimeout = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_resolve, reject) => {
        timeoutId = setTimeout(() => {
          didTimeout = true;
          const err = new Error("Request timed out");
          (err as any).name = "AbortError";
          reject(err);
        }, timeoutMs);
      });

      try {
        resp = (await Promise.race([fetchPromise, timeoutPromise])) as Response;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        if (didTimeout) {
          // Avoid unhandled rejections if the request later fails after we've timed out.
          fetchPromise.catch(() => {});
        }
      }
    } else {
      resp = await fetchPromise;
    }
  } catch (err) {
    const base = err instanceof Error ? err.message : "Network error";
    const correlationId = mergedHeaders[existingCorrelationKey || "x-correlation-id"];
    const suffix = correlationId ? ` [corr ${correlationId}]` : "";
    const isAbort = (err as any)?.name === "AbortError";
    const code = isAbort ? "timeout" : "network_error";
    const msg = isAbort ? "Request timed out" : base;
    const error: ApiError = new Error(`${msg} (${code})${suffix}`);
    error.code = code;
    error.status = 0;
    error.correlationId = correlationId;
    throw error;
  }

  const contentType = resp.headers.get("content-type") || "";
  const requestIdHeader = resp.headers.get("x-request-id") || resp.headers.get("x-request-id".toUpperCase());
  const correlationIdHeader = resp.headers.get("x-correlation-id") || resp.headers.get("x-correlation-id".toUpperCase());
  const traceIdHeader = resp.headers.get("x-trace-id") || resp.headers.get("x-trace-id".toUpperCase());
  const retryAfterHeader = resp.headers.get("retry-after") || resp.headers.get("Retry-After");
  const retryAfter = (() => {
    const raw = String(retryAfterHeader || "").trim();
    if (!raw) return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return Math.floor(n);
  })();

  if (!contentType.toLowerCase().includes("application/json")) {
    const text = await resp.text().catch(() => "");
    const code = resp.status === 401 ? "unauthorized" : "unexpected";
    const reqSuffix = requestIdHeader ? ` [req ${requestIdHeader}]` : "";
    const safeText = humanizeKnownIdsInText(text || "");
    const error: ApiError = new Error(`${safeText || "Unexpected response from server"} (${code})${reqSuffix}`);
    error.code = code;
    error.status = resp.status;
    if (requestIdHeader) error.requestId = requestIdHeader;
    if (correlationIdHeader) error.correlationId = correlationIdHeader;
    if (traceIdHeader) error.traceId = traceIdHeader;
    if (retryAfter != null) error.retryAfter = retryAfter;
    throw error;
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    const code = resp.status === 401 ? "unauthorized" : "unexpected";
    const reqSuffix = requestIdHeader ? ` [req ${requestIdHeader}]` : "";
    const error: ApiError = new Error(`Invalid JSON response from server (${code})${reqSuffix}`);
    error.code = code;
    error.status = resp.status;
    if (requestIdHeader) error.requestId = requestIdHeader;
    if (correlationIdHeader) error.correlationId = correlationIdHeader;
    if (traceIdHeader) error.traceId = traceIdHeader;
    throw error;
  }

  const resolvedRequestId = envelope?.meta?.request_id || requestIdHeader || undefined;
  const resolvedCorrelationId = envelope?.meta?.correlation_id || correlationIdHeader || undefined;
  const resolvedTraceId = envelope?.meta?.trace_id || traceIdHeader || undefined;

  lastApiContext = {
    requestId: resolvedRequestId,
    correlationId: resolvedCorrelationId,
    traceId: resolvedTraceId,
  };

  if (!resp.ok || !envelope?.ok) {
    const code = envelope?.error?.code || "unexpected";
    const message = humanizeKnownIdsInText(envelope?.error?.message || "Unexpected error");
    const reqSuffix = resolvedRequestId ? ` [req ${resolvedRequestId}]` : "";
    const error: ApiError = new Error(`${message} (${code})${reqSuffix}`);
    error.code = code;
    error.status = resp.status;
    error.requestId = resolvedRequestId;
    error.correlationId = resolvedCorrelationId;
    error.traceId = resolvedTraceId;
    if (retryAfter != null) error.retryAfter = retryAfter;

    // Best-effort: report API not_found to backend telemetry so broken routes are visible to Support.
    try {
      const isTelemetry = path.startsWith("/api/v1/telemetry/");
      if (!isTelemetry && resp.status === 404 && code === "not_found" && typeof window !== "undefined") {
        const now = Date.now();
        const key = `${path}|${options?.method || ""}`.slice(0, 300);
        if (now - lastNotFoundReportAt > 60_000) {
          recentNotFoundKeys.clear();
        }
        if (!recentNotFoundKeys.has(key) && recentNotFoundKeys.size < 50) {
          recentNotFoundKeys.add(key);
          lastNotFoundReportAt = now;
          reportClientError({
            type: "api_not_found",
            app: appSurface(),
            url: typeof window !== "undefined" ? window.location.href : "",
            message: `${options?.method || "GET"} ${path} returned not_found`,
            last_request_id: resolvedRequestId || null,
            last_correlation_id: resolvedCorrelationId || null,
            last_trace_id: resolvedTraceId || null,
            at_utc: new Date().toISOString(),
          });
        }
      }
    } catch {
      // ignore
    }
    throw error;
  }

  return envelope.data as T;
}

export async function getJson<T>(path: string): Promise<T> {
  return getJsonWithOptions<T>(path, undefined);
}

export async function getJsonWithOptions<T>(path: string, options?: GetJsonOptions): Promise<T> {
  const key = String(options?.cacheKey || path);
  const ttl = Number(options?.cacheTtlMs || 0);
  const canCache = typeof window !== "undefined" && Number.isFinite(ttl) && ttl > 0;

  const timeoutMs =
    Number.isFinite(Number(options?.timeoutMs)) && Number(options?.timeoutMs) > 0
      ? Number(options?.timeoutMs)
      : 15_000;
  const retries =
    Number.isFinite(Number(options?.retries)) && Number(options?.retries) >= 0
      ? Math.min(2, Math.max(0, Math.floor(Number(options?.retries))))
      : 1;
  const retryBaseDelayMs =
    Number.isFinite(Number(options?.retryBaseDelayMs)) && Number(options?.retryBaseDelayMs) > 0
      ? Math.min(5_000, Math.floor(Number(options?.retryBaseDelayMs)))
      : 350;

  if (canCache) {
    const cached = responseCache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value as T;
    }
  }

  const inflight = inflightGets.get(key);
  if (inflight) return inflight as Promise<T>;

  const p = (async () => {
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await requestJson<T>(path, { method: "GET", ...( { timeoutMs } as any ) });
      } catch (err) {
        const e = err as ApiError;
        const status = Number(e?.status || 0);
        const code = String(e?.code || "");
        const isRetryable =
          code === "network_error" ||
          code === "timeout" ||
          status === 502 ||
          status === 503 ||
          status === 504 ||
          status === 0;

        if (!isRetryable || attempt >= retries) throw err;

        // If the browser knows it's offline, don't waste time retrying.
        try {
          if (typeof navigator !== "undefined" && navigator.onLine === false) throw err;
        } catch {
          // ignore
        }

        const jitter = Math.floor(Math.random() * 120);
        const delay = retryBaseDelayMs * (attempt + 1) + jitter;
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    // Unreachable.
    throw new Error("unreachable");
  })()
    .then((value) => {
      if (canCache) {
        responseCache.set(key, { value, expiresAt: Date.now() + ttl });
      }
      return value;
    })
    .finally(() => {
      inflightGets.delete(key);
    });

  inflightGets.set(key, p as Promise<unknown>);
  return p;
}

export async function postJson<T, B = unknown>(path: string, body: B): Promise<T> {
  return requestJson<T>(path, { method: "POST", body: JSON.stringify(body || {}) });
}

export async function putJson<T, B = unknown>(path: string, body: B): Promise<T> {
  return requestJson<T>(path, { method: "PUT", body: JSON.stringify(body || {}) });
}
